/* Ring Sizer Tool — vanilla JS, no dependencies.
 * Flow: intro (what you need) -> side-of-screen card calibration ->
 * filled-circle measurement with a live size row -> result.
 * Physical sizes come from user calibration (bank card short side,
 * 53.98 mm) because CSS mm/in units do not match real screens.
 */
(function () {
  'use strict';

  // ---------------------------------------------------------------
  // Pure conversion math (exported for unit tests)
  // ---------------------------------------------------------------
  var CARD_LONG_MM = 85.6; // ISO/IEC 7810 ID-1
  var CARD_SHORT_MM = 53.98;
  var MIN_DIAMETER_MM = 15.5; // Kabluchka catalog range
  var MAX_DIAMETER_MM = 23.0;
  var STEP_MM = 0.5; // Ukrainian half sizes: UA size = inner diameter in mm
  var FALLBACK_PX_PER_MM = 96 / 25.4;
  var MIN_PX_PER_MM = 1.5;
  var MAX_PX_PER_MM = 12;

  var UK_LETTERS = 'ABCDEFGHIJKLMNOPQRSTUVWXYZ';

  var RingSizerMath = {
    CARD_LONG_MM: CARD_LONG_MM,
    CARD_SHORT_MM: CARD_SHORT_MM,
    MIN_DIAMETER_MM: MIN_DIAMETER_MM,
    MAX_DIAMETER_MM: MAX_DIAMETER_MM,
    STEP_MM: STEP_MM,
    FALLBACK_PX_PER_MM: FALLBACK_PX_PER_MM,
    MIN_PX_PER_MM: MIN_PX_PER_MM,
    MAX_PX_PER_MM: MAX_PX_PER_MM,

    circumferenceFromDiameter: function (diameterMm) {
      return Math.PI * diameterMm;
    },

    pxPerMmFromCard: function (cardLongSidePx) {
      return cardLongSidePx / CARD_LONG_MM;
    },

    pxPerMmFromCardShort: function (cardShortSidePx) {
      return cardShortSidePx / CARD_SHORT_MM;
    },

    usSizeFromDiameter: function (diameterMm) {
      var circumferenceMm = Math.PI * diameterMm;
      return (circumferenceMm - 36.5367) / 2.5535;
    },

    roundToQuarter: function (size) {
      return Math.round(size * 4) / 4;
    },

    formatUSSize: function (size) {
      var rounded = RingSizerMath.roundToQuarter(size);
      if (rounded < 0) return '—';
      var whole = Math.floor(rounded);
      var fraction = rounded - whole;
      if (fraction === 0) return String(whole);
      if (fraction === 0.25) return whole + '¼';
      if (fraction === 0.5) return whole + '½';
      if (fraction === 0.75) return whole + '¾';
      return rounded.toFixed(2);
    },

    euSizeFromDiameter: function (diameterMm) {
      return Math.round(Math.PI * diameterMm);
    },

    // UA size = inner diameter in mm, half-size steps (17, 17.5, ...).
    formatUASize: function (diameterMm) {
      var half = Math.round(diameterMm * 2) / 2;
      return half % 1 === 0 ? String(half) : half.toFixed(1);
    },

    // Kabluchka catalog values: UA (inner diameter in mm) -> US, EU.
    KABLUCHKA_SIZE_TABLE: {
      '15.5': ['4.75', '48.63'],
      '16':   ['5.25', '50.55'],
      '16.5': ['6',    '51.82'],
      '17':   ['6.5',  '53.10'],
      '17.5': ['7.25', '55.01'],
      '18':   ['7.75', '56.29'],
      '18.5': ['8.5',  '58.20'],
      '19':   ['9',    '59.48'],
      '19.5': ['9.5',  '60.76'],
      '20':   ['10.25','62.67'],
      '20.5': ['10.75','63.94'],
      '21':   ['11.5', '65.86'],
      '21.5': ['12',   '67.13'],
      '22':   ['12.75','69.05'],
      '22.5': ['13.25','70.32'],
      '23':   ['14',   '72.24']
    },

    catalogSizesFromDiameter: function (diameterMm) {
      var ua = RingSizerMath.formatUASize(diameterMm);
      var row = RingSizerMath.KABLUCHKA_SIZE_TABLE[ua];
      return { ua: ua, us: row ? row[0] : '—', eu: row ? row[1] : '—' };
    },

    // UK/AU letters: linear fit through the standard chart anchors
    // (US6=L½, US7=N½, US8=P½, US10=T½); table-driven top end.
    ukSizeFromDiameter: function (diameterMm) {
      var circumferenceMm = Math.PI * diameterMm;
      if (circumferenceMm >= 69.4) return 'Z+';
      if (circumferenceMm >= 68.9) return 'Z½';
      if (circumferenceMm >= 68.3) return 'Z';
      var index = (circumferenceMm - 36.25) / 1.26;
      var half = Math.round(index * 2) / 2;
      if (half < 1) return 'A';
      var letter = UK_LETTERS.charAt(Math.floor(half) - 1);
      return half % 1 === 0.5 ? letter + '½' : letter;
    },

    // JP/CN: JIS, diameter = 12.67 + size / 3 (mm).
    jpSizeFromDiameter: function (diameterMm) {
      var size = Math.round(3 * (diameterMm - 12.67));
      if (size < 1) return '<1';
      if (size > 27) return '27+';
      return String(size);
    },

    // Two decimals, nudged so the shown value never rounds across the
    // EU/ISO integer boundary (EU = round(circumference) by definition).
    formatCircumference: function (diameterMm) {
      var circ = Math.PI * diameterMm;
      var eu = Math.round(circ);
      var shown = parseFloat(circ.toFixed(2));
      if (Math.round(shown) > eu) shown -= 0.01;
      else if (Math.round(shown) < eu) shown += 0.01;
      return shown.toFixed(2);
    },

    clampDiameter: function (diameterMm) {
      if (!isFinite(diameterMm)) return MIN_DIAMETER_MM;
      return Math.min(MAX_DIAMETER_MM, Math.max(MIN_DIAMETER_MM, diameterMm));
    },

    isSameDevice: function (saved, dpr, innerWidth) {
      return (
        !!saved &&
        typeof saved.pxPerMm === 'number' &&
        isFinite(saved.pxPerMm) &&
        saved.pxPerMm >= MIN_PX_PER_MM &&
        saved.pxPerMm <= MAX_PX_PER_MM &&
        Math.abs(saved.devicePixelRatio - dpr) < 0.05 &&
        Math.abs(saved.innerWidth - innerWidth) < 40
      );
    }
  };

  if (typeof module !== 'undefined' && module.exports) {
    module.exports = RingSizerMath;
  }
  if (typeof window === 'undefined') {
    return; // Node test context.
  }
  window.RingSizerMath = RingSizerMath;

  // ---------------------------------------------------------------
  // Tool UI
  // ---------------------------------------------------------------
  // v3: earlier geometry and non-branded storage keys are intentionally
  // invalidated so stale calibration is never reused.
  var STORAGE_KEY = 'kabluchkaRingSizerCalibration_v3';

  function storageGet() {
    try {
      var raw = window.localStorage.getItem(STORAGE_KEY);
      return raw ? JSON.parse(raw) : null;
    } catch (err) { return null; }
  }
  function storageSet(value) {
    try {
      window.localStorage.setItem(STORAGE_KEY, JSON.stringify(value));
      return true;
    } catch (err) { return false; }
  }

  function viewportPair() {
    var w = window.innerWidth;
    var h = window.innerHeight;
    return w < h ? [w, h] : [h, w];
  }

  function RingSizerTool(root) {
    this.root = root;
    this.q = function (sel) { return root.querySelector(sel); };

    this.state = {
      step: 'intro',
      pxPerMm: FALLBACK_PX_PER_MM,
      calibrated: false,
      diameterMm: 17.0,
      cardShortPx: Math.round(CARD_SHORT_MM * 5.4) // real-size start (~phone density)
    };

    this.els = {
      panels: {
        intro: this.q('[data-rs-panel="intro"]'),
        calibrate: this.q('[data-rs-panel="calibrate"]'),
        measure: this.q('[data-rs-panel="measure"]'),
        result: this.q('[data-rs-panel="result"]')
      },
      card: this.q('[data-rs-card]'),
      cardStage: this.q('[data-rs-card-stage]'),
      cardSlider: this.q('[data-rs-card-slider]'),
      circleStage: this.q('[data-rs-circle-stage]'),
      circleSvg: this.q('[data-rs-circle-svg]'),
      diaSlider: this.q('[data-rs-dia-slider]'),
      sizeUA: this.q('[data-rs-size-ua]'),
      sizeUS: this.q('[data-rs-size-us]'),
      sizeEU: this.q('[data-rs-size-eu]'),
      fitWarning: this.q('[data-rs-fit-warning]'),
      zoomWarning: this.q('[data-rs-zoom-warning]'),
      rotateWarning: this.q('[data-rs-rotate-warning]'),
      resultList: this.q('[data-rs-results]'),
      copyDone: this.q('[data-rs-copy-done]'),
      copyFail: this.q('[data-rs-copy-fail]'),
      announcer: this.q('[data-rs-announcer]')
    };

    this.t = {
      millimeters: 'millimeters',
      uaSize: 'UA size',
      usSize: 'US size',
      euSize: 'EU size',
      yourRingSize: 'Your ring size',
      copyPrefix: 'Ring size:'
    };
    var i18nEl = this.q('script[data-rs-i18n]');
    if (i18nEl) {
      try {
        var parsed = JSON.parse(i18nEl.textContent);
        for (var key in parsed) {
          if (typeof parsed[key] === 'string' && parsed[key] &&
              parsed[key].indexOf('translation missing') === -1) {
            this.t[key] = parsed[key];
          }
        }
      } catch (err) { /* keep defaults */ }
    }

    this.calibViewport = null;
    this.lastAnnouncedDia = null;

    this.updateCardSliderBounds();
    this.restoreCalibration();
    if (!this.state.calibrated) {
      // Start at the LARGEST outline like the reference: the user works
      // by shrinking it down onto the card.
      this.state.cardShortPx = this.cardSliderBounds().max;
    }
    this.bind();
    this.render();
  }

  RingSizerTool.prototype.restoreCalibration = function () {
    var saved = storageGet();
    if (!saved) return;
    if (RingSizerMath.isSameDevice(saved, window.devicePixelRatio || 1, window.innerWidth)) {
      this.state.pxPerMm = saved.pxPerMm;
      this.state.calibrated = true;
      this.state.cardShortPx = Math.round(saved.pxPerMm * CARD_SHORT_MM);
      this.calibViewport = {
        pair: saved.innerHeight
          ? (saved.innerWidth < saved.innerHeight
            ? [saved.innerWidth, saved.innerHeight]
            : [saved.innerHeight, saved.innerWidth])
          : viewportPair(),
        dpr: saved.devicePixelRatio
      };
    }
  };

  RingSizerTool.prototype.persistCalibration = function () {
    storageSet({
      pxPerMm: this.state.pxPerMm,
      devicePixelRatio: window.devicePixelRatio || 1,
      innerWidth: window.innerWidth,
      innerHeight: window.innerHeight,
      savedAt: new Date().toISOString()
    });
    this.calibViewport = { pair: viewportPair(), dpr: window.devicePixelRatio || 1 };
  };

  RingSizerTool.prototype.bind = function () {
    var self = this;
    var root = this.root;

    root.addEventListener('click', function (event) {
      var btn = event.target.closest('[data-rs-action]');
      if (!btn || !root.contains(btn)) return;
      self.handleAction(btn.getAttribute('data-rs-action'));
    });

    if (this.els.cardSlider) {
      this.els.cardSlider.addEventListener('input', function () {
        self.state.cardShortPx = parseFloat(self.els.cardSlider.value);
        self.renderCard();
      });
    }
    if (this.els.diaSlider) {
      this.els.diaSlider.addEventListener('input', function () {
        self.state.diameterMm = RingSizerMath.clampDiameter(parseFloat(self.els.diaSlider.value));
        self.renderCircle();
      });
    }

    root.querySelectorAll('[data-rs-step]').forEach(function (btn) {
      self.attachHoldRepeat(btn);
    });

    this.onResize = this.debounce(function () {
      self.updateCardSliderBounds();
      self.checkViewportDrift();
      self.renderCard();
      self.renderCircle();
    }, 150);
    window.addEventListener('resize', this.onResize);
    window.addEventListener('orientationchange', this.onResize);

    if (window.visualViewport) {
      this.onVvResize = this.debounce(function () { self.checkPinchZoom(); }, 150);
      window.visualViewport.addEventListener('resize', this.onVvResize);
    }
  };

  RingSizerTool.prototype.destroy = function () {
    window.removeEventListener('resize', this.onResize);
    window.removeEventListener('orientationchange', this.onResize);
    if (window.visualViewport && this.onVvResize) {
      window.visualViewport.removeEventListener('resize', this.onVvResize);
    }
  };

  RingSizerTool.prototype.debounce = function (fn, ms) {
    var t;
    return function () { clearTimeout(t); t = setTimeout(fn, ms); };
  };

  RingSizerTool.prototype.announce = function (text) {
    var el = this.els.announcer;
    if (!el) return;
    var self = this;
    clearTimeout(this._announceClearT);
    el.textContent = '';
    this._announceClearT = setTimeout(function () {
      el.textContent = text;
      clearTimeout(self._announceResetT);
      self._announceResetT = setTimeout(function () { el.textContent = ''; }, 4000);
    }, 100);
  };

  RingSizerTool.prototype.attachHoldRepeat = function (btn) {
    var self = this;
    var timer = null, interval = null, repeated = false;
    function stop() {
      clearTimeout(timer); clearInterval(interval);
      timer = null; interval = null;
    }
    btn.addEventListener('pointerdown', function (event) {
      if (event.button !== 0) return;
      stop();
      repeated = false;
      timer = setTimeout(function () {
        interval = setInterval(function () {
          repeated = true;
          self.applyStep(btn);
        }, 120);
      }, 450);
    });
    ['pointerup', 'pointerleave', 'pointercancel'].forEach(function (type) {
      btn.addEventListener(type, stop);
    });
    btn.addEventListener('click', function () {
      if (repeated) { repeated = false; return; }
      self.applyStep(btn);
    });
  };

  RingSizerTool.prototype.applyStep = function (btn) {
    var target = btn.getAttribute('data-rs-step-target');
    var dir = btn.getAttribute('data-rs-step') === 'up' ? 1 : -1;
    if (target === 'card') {
      var slider = this.els.cardSlider;
      var next = this.state.cardShortPx + dir * parseFloat(slider.step || '1');
      next = Math.min(parseFloat(slider.max), Math.max(parseFloat(slider.min), next));
      this.state.cardShortPx = next;
      this.renderCard();
    } else {
      var mm = this.state.diameterMm + dir * STEP_MM;
      this.state.diameterMm = RingSizerMath.clampDiameter(Math.round(mm * 2) / 2);
      this.renderCircle();
    }
  };

  RingSizerTool.prototype.handleAction = function (action) {
    switch (action) {
      case 'intro-next':
        this.state.step = 'calibrate';
        break;
      case 'cal-back':
        this.state.step = 'intro';
        break;
      case 'cal-next':
        this.state.pxPerMm = RingSizerMath.pxPerMmFromCardShort(this.state.cardShortPx);
        this.state.calibrated = true;
        this.persistCalibration();
        this.state.step = 'measure';
        break;
      case 'mea-back':
        this.state.step = 'calibrate';
        break;
      case 'mea-next':
        this.state.step = 'result';
        break;
      case 'measure-again':
        this.state.step = 'measure';
        break;
      case 'copy-size':
        this.copySize();
        return;
      default:
        return;
    }
    this.render();
    this.focusCurrentPanel();
  };

  RingSizerTool.prototype.focusCurrentPanel = function () {
    var panel = this.els.panels[this.state.step];
    if (!panel) return;
    var title = panel.querySelector('[data-rs-focus]');
    if (title) title.focus();
  };

  RingSizerTool.prototype.checkViewportDrift = function () {
    if (!this.state.calibrated || !this.calibViewport) return;
    var now = viewportPair();
    var was = this.calibViewport.pair;
    // The long dimension jumps by ~60-120px on phones when the browser
    // chrome collapses/expands, so it gets a loose threshold; the short
    // dimension and DPR are the real "different screen" signals.
    var drifted =
      Math.abs((window.devicePixelRatio || 1) - this.calibViewport.dpr) >= 0.05 ||
      Math.abs(now[0] - was[0]) >= 40 ||
      Math.abs(now[1] - was[1]) >= 160;
    this.toggleWarning(this.els.rotateWarning, drifted && this.state.step === 'measure');
  };

  RingSizerTool.prototype.checkPinchZoom = function () {
    if (!window.visualViewport) return;
    var zoomed = Math.abs(window.visualViewport.scale - 1) > 0.02;
    this.toggleWarning(this.els.zoomWarning, zoomed);
  };

  RingSizerTool.prototype.toggle = function (el, show) {
    if (el) el.hidden = !show;
  };

  RingSizerTool.prototype.toggleWarning = function (el, show) {
    if (!el) return;
    var wasHidden = el.hidden;
    el.hidden = !show;
    if (show && wasHidden) this.announce(el.textContent.trim());
  };

  // ------------------------------- rendering

  RingSizerTool.prototype.render = function () {
    var step = this.state.step;
    for (var name in this.els.panels) {
      var panel = this.els.panels[name];
      if (panel) panel.hidden = name !== step;
    }
    if (step === 'calibrate') this.renderCard();
    if (step === 'measure') this.renderCircle();
    if (step === 'result') this.renderResult();
    this.checkViewportDrift();
    this.checkPinchZoom();
  };

  RingSizerTool.prototype.updateCardSliderBounds = function () {
    var slider = this.els.cardSlider;
    if (!slider) return;
    // Reference geometry: the outline's TOP edge is fixed, the slider
    // grows its HEIGHT downward; that height is matched to the card's
    // short (53.98 mm) side with the card lying horizontally. 380px
    // covers ~7 px/mm — denser than any phone.
    var max = 380;
    slider.max = String(max);
    if (this.els.cardStage) this.els.cardStage.style.height = (max + 24) + 'px';
  };

  RingSizerTool.prototype.cardSliderBounds = function () {
    var slider = this.els.cardSlider;
    return {
      min: slider ? parseFloat(slider.min) : 150,
      max: slider ? parseFloat(slider.max) : 460
    };
  };

  // The card outline is anchored to the LEFT EDGE of the screen; the user
  // hangs the card off the side of the phone and matches its short
  // (53.98 mm) edge to the outline width.
  RingSizerTool.prototype.renderCard = function () {
    var els = this.els;
    if (!els.card) return;
    var bounds = this.cardSliderBounds();
    this.state.cardShortPx = Math.min(bounds.max, Math.max(bounds.min, this.state.cardShortPx));
    var shortPx = this.state.cardShortPx;

    // CSS anchors the box at left:-20px (bleed off-screen); add those 20px
    // back so the VISIBLE width from the screen edge is exactly shortPx.
    // Top edge pinned, bottom edge follows the slider: the HEIGHT is the
    // measured 53.98 mm; the width is fixed and irrelevant (the card
    // hangs off the left side of the screen).
    els.card.style.height = shortPx + 'px';

    if (els.cardSlider) {
      els.cardSlider.value = String(shortPx);
      var mmPreview = (shortPx / CARD_SHORT_MM).toFixed(2);
      els.cardSlider.setAttribute(
        'aria-valuetext',
        Math.round(shortPx) + ' px = 53.98 ' + this.t.millimeters + ' (' + mmPreview + ' px/mm)'
      );
      // висота контуру = вимірювана величина
    }
  };

  RingSizerTool.prototype.sizesForCurrent = function () {
    return RingSizerMath.catalogSizesFromDiameter(this.state.diameterMm);
  };

  RingSizerTool.prototype.renderCircle = function () {
    var els = this.els;
    if (!els.circleSvg) return;
    var mm = this.state.diameterMm;
    var pxPerMm = this.state.pxPerMm;
    var diaPx = mm * pxPerMm;

    // Fixed stage per calibration: the circle grows inside it, the page
    // never reflows while adjusting.
    var stagePx = Math.ceil(MAX_DIAMETER_MM * pxPerMm + 16);
    var available = (els.circleStage ? els.circleStage.clientWidth : 0) || window.innerWidth;
    this.toggleWarning(els.fitWarning, diaPx > available - 8);

    var c = stagePx / 2;
    els.circleSvg.setAttribute('viewBox', '0 0 ' + stagePx + ' ' + stagePx);
    els.circleSvg.setAttribute('width', stagePx);
    els.circleSvg.setAttribute('height', stagePx);
    els.circleSvg.innerHTML =
      '<circle class="ring-sizer-tool__disc" cx="' + c + '" cy="' + c + '" r="' + (diaPx / 2) + '"></circle>';

    if (els.diaSlider) {
      els.diaSlider.value = String(mm);
      els.diaSlider.setAttribute(
        'aria-valuetext',
        this.t.uaSize + ' ' + RingSizerMath.formatUASize(mm) + ' — ' + mm.toFixed(1) + ' ' + this.t.millimeters
      );
    }

    var s = this.sizesForCurrent();
    if (els.sizeUA) els.sizeUA.textContent = s.ua;
    if (els.sizeUS) els.sizeUS.textContent = s.us;
    if (els.sizeEU) els.sizeEU.textContent = s.eu;

    var self = this;
    clearTimeout(this._diaAnnounceT);
    if (this.state.step === 'measure' && this.lastAnnouncedDia !== null &&
        this.lastAnnouncedDia !== mm) {
      this._diaAnnounceT = setTimeout(function () {
        self.announce(self.t.uaSize + ' ' + RingSizerMath.formatUASize(mm));
        self.lastAnnouncedDia = mm;
      }, 600);
    } else {
      this.lastAnnouncedDia = mm;
    }
  };

  RingSizerTool.prototype.renderResult = function () {
    var list = this.els.resultList;
    if (!list) return;
    var s = this.sizesForCurrent();
    var rows = [
      [this.t.uaSize, s.ua],
      [this.t.usSize, s.us],
      [this.t.euSize, s.eu]
    ];
    list.innerHTML = rows.map(function (row) {
      return '<div class="ring-sizer-tool__result-row"><dt>' + row[0] + '</dt><dd>' + row[1] + '</dd></div>';
    }).join('');
    if (this.els.copyDone) this.els.copyDone.hidden = true;
    if (this.els.copyFail) this.els.copyFail.hidden = true;
    this.announce(this.t.yourRingSize + ': UA ' + s.ua + ', US ' + s.us + ', EU ' + s.eu);
  };

  RingSizerTool.prototype.copySize = function () {
    var s = this.sizesForCurrent();
    var text = this.t.copyPrefix + ' UA ' + s.ua + ' / US ' + s.us + ' / EU ' + s.eu;
    var self = this;

    function feedback(ok) {
      if (self.els.copyDone) self.els.copyDone.hidden = !ok;
      if (self.els.copyFail) self.els.copyFail.hidden = ok;
      var el = ok ? self.els.copyDone : self.els.copyFail;
      if (el) self.announce(el.textContent.trim());
      clearTimeout(self._copyT);
      self._copyT = setTimeout(function () {
        if (self.els.copyDone) self.els.copyDone.hidden = true;
        if (self.els.copyFail) self.els.copyFail.hidden = true;
      }, 4000);
    }

    if (navigator.clipboard && navigator.clipboard.writeText) {
      navigator.clipboard.writeText(text).then(
        function () { feedback(true); },
        function () { feedback(legacyCopy(text)); }
      );
    } else {
      feedback(legacyCopy(text));
    }
  };

  function legacyCopy(text) {
    var previousFocus = document.activeElement;
    var area = document.createElement('textarea');
    area.value = text;
    area.setAttribute('readonly', '');
    area.style.position = 'fixed';
    area.style.left = '-9999px';
    document.body.appendChild(area);
    area.select();
    var ok = false;
    try { ok = document.execCommand('copy'); } catch (err) { ok = false; }
    document.body.removeChild(area);
    if (previousFocus && previousFocus.focus) previousFocus.focus();
    return ok;
  }

  // ------------------------------- boot

  function initAll(scope) {
    (scope || document).querySelectorAll('[data-ring-sizer]').forEach(function (root) {
      if (root.__ringSizer) return;
      root.__ringSizer = new RingSizerTool(root);
    });
    syncFocusedMode();
  }

  function syncFocusedMode() {
    var focusedTool = document.querySelector('[data-ring-sizer][data-focused-mode="true"]');
    document.body.classList.toggle('ring-sizer-focused-mode', !!focusedTool);
  }

  if (document.readyState === 'loading') {
    document.addEventListener('DOMContentLoaded', function () { initAll(); });
  } else {
    initAll();
  }

  document.addEventListener('shopify:section:load', function (event) {
    initAll(event.target);
  });
  document.addEventListener('shopify:section:unload', function (event) {
    var root = event.target.querySelector('[data-ring-sizer]');
    if (root && root.__ringSizer) {
      root.__ringSizer.destroy();
      delete root.__ringSizer;
    }
    if (root) root.setAttribute('data-focused-mode', 'false');
    syncFocusedMode();
  });

  window.RingSizerTool = RingSizerTool;
})();
